import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, HelpCircle, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  setData,
  setCurrentTab,
  setUserInitiatedNav,
} from "../../../store/slices/newLoomSlice";
import { MultiSelect } from "../../../components/ui/multiselect";
import api from "@/utils/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDispatch, useSelector } from "react-redux";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import FilePreview from "../../../components/common/FilePreview";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import sampleImage from "../../../assets/sample_gps_image.jpeg";

const particularsSchema = (user) =>
  z
    .object({
      membership: z.string().min(1, "Organization Type is required"),
      registration_type: z.string().optional(),

      udyog_aadhaar_copy: z.any().optional(),
      ssi_copy: z.any().optional(),
      other_valid_documents: z.any().optional(),

      loom_shed_name: z.string().min(1, "Loom Shed Name is required"),
      loom_shed_address: z.string().min(1, "Loom Shed Address is required"),
      loom_shed_pincode: z
        .string()
        .regex(/^\d{6}$/, "Enter valid 6-digit pincode"),

      geo_tag: z
        .any()
        .refine(
          (file) => file instanceof File || typeof file === "string",
          "Geo Tag is required"
        ),

      eb_service_number: z.string().min(1, "EB Service SC Number is required"),
      free_eb_availed: z.string().min(1, "Free EB availed or Not is required"),
      // units_used: z.string().optional(),

      electricity_bill_owner_name: z
        .string()
        .min(1, "Electricity Bill Owner Name is required"),
      electricity_bill_attachment: z.any().optional(),

      has_other_schemes: z
        .string()
        .min(1, "Employment Benefits field is required"),
      other_state_gov_scheme: z
        .string()
        .min(1, "State/Central Scheme field is required"),
      other_scheme_details: z.string().optional(),
      scheme_documents: z.any().optional(),
      premises_type: z.string().optional(),
      variety_produced: z.array(z.string()).optional(),
      other_variety: z.string().optional(),
      rapier_looms: z.string().min(1, "Ordinary Powerlooms is required"),
      // cost_paid_for_loom: z.string().optional(),
      rental_lease_upload: z.any().optional(),
    })
    .superRefine((data, ctx) => {
      if (
        user?.organization_type == "Private Firm / Partnership" ||
        user?.organization_type == "Proprietor"
      ) {
        if (!data?.registration_type) {
          ctx.addIssue({
            path: ["registration_type"],
            code: z.ZodIssueCode.custom,
            message: "Registration Proof is required",
          });
        }
      }
      if (data.registration_type === "Udyog Aadhaar") {
        const valid =
          data.udyog_aadhaar_copy instanceof File ||
          (typeof data.udyog_aadhaar_copy === "string" &&
            data.udyog_aadhaar_copy.length > 0);
        if (!valid) {
          ctx.addIssue({
            path: ["udyog_aadhaar_copy"],
            code: z.ZodIssueCode.custom,
            message: "Udyog Aadhaar attachment is required",
          });
        }
      }

      if (data.registration_type === "SSI / MSME Registration") {
        const valid =
          data.ssi_copy instanceof File ||
          (typeof data.ssi_copy === "string" && data.ssi_copy.length > 0);
        if (!valid) {
          ctx.addIssue({
            path: ["ssi_copy"],
            code: z.ZodIssueCode.custom,
            message: "SSI / MSME Registration Proof is required",
          });
        }
      }

      if (data.registration_type === "Other Valid Documents") {
        const valid =
          data.other_valid_documents instanceof File ||
          (typeof data.other_valid_documents === "string" &&
            data.other_valid_documents.length > 0);
        if (!valid) {
          ctx.addIssue({
            path: ["other_valid_documents"],
            code: z.ZodIssueCode.custom,
            message: "Other valid document is required",
          });
        }
      }

      if (
        data.other_state_gov_scheme &&
        data.other_state_gov_scheme !== "None of the above"
      ) {
        if (!data.other_scheme_details?.trim()) {
          ctx.addIssue({
            path: ["other_scheme_details"],
            code: z.ZodIssueCode.custom,
            message: "Specify Scheme Details is required",
          });
        }

        const validDoc =
          data.scheme_documents instanceof File ||
          (typeof data.scheme_documents === "string" &&
            data.scheme_documents.length > 0);
        if (!validDoc) {
          ctx.addIssue({
            path: ["scheme_documents"],
            code: z.ZodIssueCode.custom,
            message: "Upload Benefited Avail Scheme Documents is required",
          });
        }
      }

      if (data.premises_type && data.premises_type !== "Own Premises") {
        const valid =
          data.rental_lease_upload instanceof File ||
          (typeof data.rental_lease_upload === "string" &&
            data.rental_lease_upload.length > 0);
        if (!valid) {
          ctx.addIssue({
            path: ["rental_lease_upload"],
            code: z.ZodIssueCode.custom,
            message: "Upload Rental / Lease Premises Documents is required",
          });
        }
      }

      if (
        data.variety_produced?.includes("Any Other (Specify)") &&
        !data.other_variety?.trim()
      ) {
        ctx.addIssue({
          path: ["other_variety"],
          code: z.ZodIssueCode.custom,
          message: "Please specify the other variety produced",
        });
      }
    });

const LoomDetails = ({ data = {} }) => {
  const [filePreviews, setFilePreviews] = useState({});
  const user = useSelector((state) => state.auth.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const dispatch = useDispatch();
  const nav = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(particularsSchema(user)),
    defaultValues: {
      membership:
        data?.beneficiary?.membership ?? user?.organization_type ?? "",
      registration_type: data?.beneficiary?.registration_type ?? "",

      loom_shed_name: data?.beneficiary?.loom_shed_name ?? "",
      loom_shed_address: data?.beneficiary?.loom_shed_address ?? "",
      loom_shed_pincode: data?.beneficiary?.loom_shed_pincode ?? "",

      eb_service_number: data?.beneficiary?.eb_service_number ?? "",
      free_eb_availed: data?.beneficiary?.free_eb_availed ?? "",
      // units_used: data?.beneficiary?.units_used ?? "",
      electricity_bill_owner_name:
        data?.beneficiary?.electricity_bill_owner_name ?? "",
      electricity_bill_attachment:
        data?.beneficiary?.electricity_bill_attachment ?? undefined,
      udyog_aadhaar_copy: data?.beneficiary?.udyog_aadhaar_copy ?? undefined,
      ssi_copy: data?.beneficiary?.ssi_copy ?? undefined,
      other_valid_documents:
        data?.beneficiary?.other_valid_documents ?? undefined,
      geo_tag: data?.beneficiary?.geo_tag ?? undefined,

      has_other_schemes: data?.beneficiary?.has_other_schemes ?? "",
      other_state_gov_scheme: data?.beneficiary?.other_state_gov_scheme ?? "",
      other_scheme_details: data?.beneficiary?.other_scheme_details ?? "",
      rapier_looms: data?.beneficiary?.rapier_looms ?? "",
      variety_produced: data?.beneficiary?.variety_produced ?? [],
      other_variety: data?.beneficiary?.other_variety ?? "",
      premises_type: data?.beneficiary?.premises_type ?? "",
      rental_lease_upload: data?.beneficiary?.rental_lease_upload ?? undefined,
      scheme_documents: data?.beneficiary?.scheme_documents ?? undefined,
    },
  });

  const formData = watch();

  useEffect(() => {
    if (data) {
      const previews = {
        electricity_bill_attachment:
          data?.beneficiary?.electricity_bill_attachment ?? undefined,
        udyog_aadhaar_copy: data?.beneficiary?.udyog_aadhaar_copy ?? undefined,
        ssi_copy: data?.beneficiary?.ssi_copy ?? undefined,
        other_valid_documents:
          data?.beneficiary?.other_valid_documents ?? undefined,
        geo_tag: data?.beneficiary?.geo_tag ?? undefined,
        scheme_documents: data?.beneficiary?.scheme_documents ?? undefined,
        rental_lease_upload:
          data?.beneficiary?.rental_lease_upload ?? undefined,
      };
      setFilePreviews(previews);
    }
  }, [data]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      if (name === "geo_tag" && file.type === "application/pdf") {
        alert(
          "PDF files are not allowed for geo tag. Please upload an image file."
        );
        return;
      }

      setValue(name, file, { shouldValidate: true });

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews((prev) => ({ ...prev, [name]: e.target.result }));
        };
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          submitData.append(key, JSON.stringify(value));
        } else if (value instanceof File) {
          submitData.append(key, value);
        } else if (value !== undefined && value !== null) {
          submitData.append(key, value);
        }
      });

      submitData.append("id", data.id);

      const response = await api.post(
        "/new_shuttleless/beneficiary-loom",
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      dispatch(setData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application Saved successfully",
        variant: "success",
      });

      searchParams.delete("tab");

      dispatch(setUserInitiatedNav(true));
      if (buttonType === "next") {
        dispatch(setCurrentTab("bank"));
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardContent className="space-y-6 mt-5">
          <CardHeader className="flex items-center justify-between flex-row">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <CardTitle>Beneficiary Details</CardTitle>
            </div>
          </CardHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="membership" required>
                Organization Type
              </Label>
              <Input
                id="membership"
                {...register("membership")}
                onChange={(e) =>
                  handleInputChange("membership", e.target.value)
                }
                className={`${
                  errors.membership ? "border-red-500" : ""
                } cursor-not-allowed bg-muted`}
                style={{ pointerEvents: "none" }}
                readOnly
              />
              {errors.membership && (
                <p className="text-sm text-red-500">
                  {errors.membership.message}
                </p>
              )}
            </div>

            {(user?.organization_type == "Private Firm / Partnership" ||
              user?.organization_type == "Proprietor") && (
              <div className="space-y-2">
                <Label htmlFor="registration_type" required>
                  Registration Proof
                </Label>
                <Select
                  value={formData.registration_type || ""}
                  onValueChange={(value) =>
                    handleInputChange("registration_type", value)
                  }
                >
                  <SelectTrigger
                    className={errors.registration_type ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select Any one Field" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SSI / MSME Registration">
                      SSI / MSME Registration
                    </SelectItem>
                    <SelectItem value="Udyog Aadhaar">Udyog Aadhaar</SelectItem>
                    <SelectItem value="Other Valid Documents">
                      Other Valid Documents
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.registration_type && (
                  <p className="text-sm text-red-500">
                    {errors.registration_type.message}
                  </p>
                )}
              </div>
            )}

            {(user?.organization_type == "Private Firm / Partnership" ||
              user?.organization_type == "Proprietor") &&
              formData.registration_type === "Udyog Aadhaar" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="udyog_aadhaar_copy" required>
                      Upload Udyog Aadhaar Proof
                    </Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      <Input
                        id="udyog_aadhaar_copy"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) =>
                          handleFileUpload(
                            "udyog_aadhaar_copy",
                            e.target.files[0]
                          )
                        }
                        className={
                          errors.udyog_aadhaar_copy ? "border-red-500" : ""
                        }
                      />

                      <FilePreview
                        fieldName="udyog_aadhaar_copy"
                        preview={filePreviews.udyog_aadhaar_copy}
                        file={
                          formData.udyog_aadhaar_copy instanceof File
                            ? formData.udyog_aadhaar_copy
                            : null
                        }
                        existingFile={
                          typeof filePreviews.udyog_aadhaar_copy === "string"
                            ? filePreviews.udyog_aadhaar_copy
                            : null
                        }
                        onRemove={handleRemoveFile}
                      />

                      {errors.udyog_aadhaar_copy && (
                        <p className="text-sm text-red-500">
                          {errors.udyog_aadhaar_copy.message}
                        </p>
                      )}

                      <p className="ms-1 text-[12px] text-muted-foreground">
                        Supported only JPG, JPEG, PNG, PDF Files max size 2MB
                      </p>
                    </div>
                  </div>
                </>
              )}

            {(user?.organization_type == "Private Firm / Partnership" ||
              user?.organization_type == "Proprietor") &&
              formData.registration_type === "SSI / MSME Registration" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="ssi_copy" required>
                      Upload SSI / MSME Registration Proof
                    </Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      <Input
                        id="ssi_copy"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) =>
                          handleFileUpload("ssi_copy", e.target.files[0])
                        }
                        className={errors.ssi_copy ? "border-red-500" : ""}
                      />

                      <FilePreview
                        fieldName="ssi_copy"
                        preview={filePreviews.ssi_copy}
                        file={
                          formData.ssi_copy instanceof File
                            ? formData.ssi_copy
                            : null
                        }
                        existingFile={
                          typeof filePreviews.ssi_copy === "string"
                            ? filePreviews.ssi_copy
                            : null
                        }
                        onRemove={handleRemoveFile}
                      />

                      {errors.ssi_copy && (
                        <p className="text-sm text-red-500">
                          {errors.ssi_copy.message}
                        </p>
                      )}

                      <p className="ms-1 text-[12px] text-muted-foreground">
                        Supported only JPG, JPEG, PNG, PDF Files max size 2MB
                      </p>
                    </div>
                  </div>
                </>
              )}

            {(user?.organization_type == "Private Firm / Partnership" ||
              user?.organization_type == "Proprietor") &&
              formData.registration_type === "Other Valid Documents" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="other_valid_documents" required>
                      Other Valid Registration Documents Uploads
                    </Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      <Input
                        id="other_valid_documents"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) =>
                          handleFileUpload(
                            "other_valid_documents",
                            e.target.files[0]
                          )
                        }
                        className={
                          errors.other_valid_documents ? "border-red-500" : ""
                        }
                      />

                      <FilePreview
                        fieldName="other_valid_documents"
                        preview={filePreviews.other_valid_documents}
                        file={
                          formData.other_valid_documents instanceof File
                            ? formData.other_valid_documents
                            : null
                        }
                        existingFile={
                          typeof filePreviews.other_valid_documents === "string"
                            ? filePreviews.other_valid_documents
                            : null
                        }
                        onRemove={handleRemoveFile}
                      />

                      {errors.other_valid_documents && (
                        <p className="text-sm text-red-500">
                          {errors.other_valid_documents.message}
                        </p>
                      )}

                      <p className="ms-1 text-[12px] text-muted-foreground">
                        Supported only JPG, JPEG, PNG, PDF Files max size 2MB
                      </p>
                    </div>
                  </div>
                </>
              )}
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="loom_shed_name" required>
                Loom Shed Name
              </Label>
              <Input
                id="loom_shed_name"
                {...register("loom_shed_name")}
                onChange={(e) =>
                  handleInputChange("loom_shed_name", e.target.value)
                }
                className={errors.loom_shed_name ? "border-red-500" : ""}
              />
              {errors.loom_shed_name && (
                <p className="text-sm text-red-500">
                  {errors.loom_shed_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="loom_shed_address" required>
                Loom Address
              </Label>
              <Textarea
                id="loom_shed_address"
                {...register("loom_shed_address")}
                onChange={(e) =>
                  handleInputChange("loom_shed_address", e.target.value)
                }
              />
              {errors.loom_shed_address && (
                <p className="text-sm text-red-500">
                  {errors.loom_shed_address.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="loom_shed_pincode" required>
                Loom Shed PIN Code
              </Label>
              <Input
                id="loom_shed_pincode"
                {...register("loom_shed_pincode")}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  handleInputChange("loom_shed_pincode", value);
                }}
                className={errors.loom_shed_pincode ? "border-red-500" : ""}
                placeholder="6-digit pincode"
              />
              {errors.loom_shed_pincode && (
                <p className="text-sm text-red-500">
                  {errors.loom_shed_pincode.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="geo_tag"
                required
                className="flex items-center gap-1"
              >
                <span>GPS Location of the Machine</span>
                <Popover>
                  <PopoverTrigger>
                    <HelpCircle className="text-primary p-0 m-0" />
                  </PopoverTrigger>
                  <PopoverContent>
                    Install the Geo tag application in your android device{" "}
                    <a
                      href="https://play.google.com/store/apps/details?id=com.gpsmapcamera.geotagginglocationonphoto"
                      target="_blank"
                      className="text-blue-500 underline"
                    >
                      (Click Here)
                    </a>{" "}
                    and capture the photo to upload
                  </PopoverContent>
                </Popover>
                <Button
                  type="button"
                  variant="link"
                  onClick={() =>
                    handleDownload(sampleImage, `sampleImage.jpeg`)
                  }
                  className="p-0 m-0"
                >
                  (Sample Image)
                </Button>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="geo_tag"
                  type="file"
                  accept=".jpeg,.jpg,.png"
                  onChange={(e) =>
                    handleFileUpload("geo_tag", e.target.files[0])
                  }
                  className={errors.geo_tag ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="geo_tag"
                  preview={filePreviews.geo_tag}
                  file={
                    formData.geo_tag instanceof File ? formData.geo_tag : null
                  }
                  existingFile={
                    typeof filePreviews.geo_tag === "string"
                      ? filePreviews.geo_tag
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                <p className="text-[12px] text-muted-foreground mt-2">
                  Supported: JPG, JPEG, PNG (max 2MB)
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="eb_service_number" required>
                EB Service SC Number
              </Label>
              <Input
                id="eb_service_number"
                {...register("eb_service_number")}
                onChange={(e) => {
                  const value = e.target.value;
                  const filteredValue = value.replace(/[^0-9-]/g, "");
                  handleInputChange("eb_service_number", filteredValue);
                }}
                className={errors.eb_service_number ? "border-red-500" : ""}
                placeholder="Enter EB Service SC Number"
              />
              {errors.eb_service_number && (
                <p className="text-sm text-red-500">
                  {errors.eb_service_number.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="free_eb_availed" required>
                Free EB availed or Not
              </Label>
              <Select
                value={formData.free_eb_availed}
                onValueChange={(value) =>
                  setValue("free_eb_availed", value, { shouldValidate: true })
                }
              >
                <SelectTrigger
                  className={errors.free_eb_availed ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Yes or No EB availed" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.free_eb_availed && (
                <p className="text-sm text-red-500">
                  {errors.free_eb_availed.message}
                </p>
              )}
            </div>

            {/* {formData.free_eb_availed && formData.free_eb_availed == "Yes" && (
              <div className="space-y-2">
                <Label htmlFor="units_used" required>
                  Provide Average EB Units Availed
                </Label>
                <Select
                  value={formData.units_used || ""}
                  onValueChange={(value) =>
                    handleInputChange("units_used", value)
                  }
                >
                  <SelectTrigger
                    className={errors.units_used ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select Any one Field" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Less Than 1000 Units">
                      Less Than 1000 Units
                    </SelectItem>
                    <SelectItem value="More Than 1000 Units">
                      More Than 1000 Units
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.units_used && (
                  <p className="text-sm text-red-500">
                    {errors.units_used.message}
                  </p>
                )}
              </div>
            )} */}

            <div className="space-y-2">
              <Label htmlFor="electricity_bill_owner_name" required>
                Electricity Connection Name
              </Label>
              <Input
                id="electricity_bill_owner_name"
                {...register("electricity_bill_owner_name")}
                onChange={(e) => {
                  const value = e.target.value;
                  const filteredValue = value.replace(/[^a-zA-Z.\s]/g, "");
                  handleInputChange(
                    "electricity_bill_owner_name",
                    filteredValue
                  );
                }}
                className={
                  errors.electricity_bill_owner_name ? "border-red-500" : ""
                }
              />
              {errors.electricity_bill_owner_name && (
                <p className="text-sm text-red-500">
                  {errors.electricity_bill_owner_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="electricity_bill_attachment" required>
                Upload Electricity Bill Proof
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="electricity_bill_attachment"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) =>
                    handleFileUpload(
                      "electricity_bill_attachment",
                      e.target.files[0]
                    )
                  }
                  className={
                    errors.electricity_bill_attachment ? "border-red-500" : ""
                  }
                />

                <FilePreview
                  fieldName="electricity_bill_attachment"
                  preview={filePreviews.electricity_bill_attachment}
                  file={
                    formData.electricity_bill_attachment instanceof File
                      ? formData.electricity_bill_attachment
                      : null
                  }
                  existingFile={
                    typeof filePreviews.electricity_bill_attachment === "string"
                      ? filePreviews.electricity_bill_attachment
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.electricity_bill_attachment && (
                  <p className="text-sm text-red-500">
                    {errors.electricity_bill_attachment.message}
                  </p>
                )}

                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only JPG, JPEG, PNG, PDF Files max size 2MB
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="has_other_schemes" required>
                Employment Benefits under Saree / Dhoti or Uniform Schemes?{" "}
              </Label>
              <Select
                value={formData.has_other_schemes || ""}
                onValueChange={(value) => {
                  handleInputChange("has_other_schemes", value);
                }}
              >
                <SelectTrigger
                  className={errors.has_other_schemes ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.has_other_schemes && (
                <p className="text-sm text-red-500">
                  {errors.has_other_schemes.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="other_state_gov_scheme" required>
                Any Other Benefits Received from State / Central Government
                Schemes
              </Label>
              <Select
                value={formData.other_state_gov_scheme || ""}
                onValueChange={(value) =>
                  handleInputChange("other_state_gov_scheme", value)
                }
              >
                <SelectTrigger
                  className={
                    errors.other_state_gov_scheme ? "border-red-500" : ""
                  }
                >
                  <SelectValue placeholder="Select Any one Field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SSI / MSME">SSI / MSME</SelectItem>
                  <SelectItem value="Industries Department">
                    Industries Department
                  </SelectItem>
                  <SelectItem value="Any Other Scheme">
                    Any Other Scheme
                  </SelectItem>
                  <SelectItem value="None of the above">
                    None of the above
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.other_state_gov_scheme && (
                <p className="text-sm text-red-500">
                  {errors.other_state_gov_scheme.message}
                </p>
              )}
            </div>

            {formData?.other_state_gov_scheme &&
              formData?.other_state_gov_scheme != "None of the above" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="other_scheme_details" required>
                      Specify Scheme Details{" "}
                    </Label>
                    <Input
                      id="other_scheme_details"
                      {...register("other_scheme_details")}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange("other_scheme_details", value);
                      }}
                      className={
                        errors.other_scheme_details ? "border-red-500" : ""
                      }
                    />
                    {errors.other_scheme_details && (
                      <p className="text-sm text-red-500">
                        {errors.other_scheme_details.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="scheme_documents" required>
                      Upload Benefited Avail Scheme Documents
                    </Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      <Input
                        id="scheme_documents"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) =>
                          handleFileUpload(
                            "scheme_documents",
                            e.target.files[0]
                          )
                        }
                        className={
                          errors.scheme_documents ? "border-red-500" : ""
                        }
                      />

                      <FilePreview
                        fieldName="scheme_documents"
                        preview={filePreviews.scheme_documents}
                        file={
                          formData.scheme_documents instanceof File
                            ? formData.scheme_documents
                            : null
                        }
                        existingFile={
                          typeof filePreviews.scheme_documents === "string"
                            ? filePreviews.scheme_documents
                            : null
                        }
                        onRemove={handleRemoveFile}
                      />

                      {errors.scheme_documents && (
                        <p className="text-sm text-red-500">
                          {errors.scheme_documents.message}
                        </p>
                      )}

                      <p className="ms-1 text-[12px] text-muted-foreground">
                        Supported only JPG, JPEG, PNG, PDF Files max size 2MB
                      </p>
                    </div>
                  </div>
                </>
              )}
          </div>

          <Separator />

          {/* <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Loom Details
            </CardTitle>
          </CardHeader> */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="premises_type">Premises Type</Label>
              <Select
                value={formData.premises_type || ""}
                onValueChange={(value) =>
                  handleInputChange("premises_type", value)
                }
              >
                <SelectTrigger
                  className={errors.premises_type ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Premises Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Own Premises">Own Premises</SelectItem>
                  <SelectItem value="Rented Premises">
                    Rented Premises
                  </SelectItem>
                  <SelectItem value="Leased Premises">
                    Leased Premises
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.premises_type && (
                <p className="text-sm text-red-500">
                  {errors.premises_type.message}
                </p>
              )}
            </div>

            {formData?.premises_type &&
              formData?.premises_type != "Own Premises" && (
                <div className="space-y-2">
                  <Label
                    htmlFor="rental_lease_upload"
                    required={
                      formData?.premises_type &&
                      formData?.premises_type != "Own Premises"
                    }
                  >
                    Upload Rental / Lease Premises Documents{" "}
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <Input
                      id="rental_lease_upload"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) =>
                        handleFileUpload(
                          "rental_lease_upload",
                          e.target.files[0]
                        )
                      }
                      className={
                        errors.rental_lease_upload ? "border-red-500" : ""
                      }
                    />
                    <FilePreview
                      fieldName="rental_lease_upload"
                      preview={filePreviews.rental_lease_upload}
                      file={
                        formData.rental_lease_upload instanceof File
                          ? formData.rental_lease_upload
                          : null
                      }
                      existingFile={
                        typeof filePreviews.rental_lease_upload === "string"
                          ? filePreviews.rental_lease_upload
                          : null
                      }
                      onRemove={handleRemoveFile}
                    />
                    {errors.rental_lease_upload && (
                      <p className="text-sm text-red-500">
                        {errors.rental_lease_upload.message}
                      </p>
                    )}
                    <p className="text-[12px] text-muted-foreground mt-2">
                      Supported: JPG, JPEG, PNG, PDF (max 2MB)
                    </p>
                  </div>
                </div>
              )}
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="variety_produced">Name of Variety Produced</Label>
              <MultiSelect
                options={[
                  { id: "Saree", name: "Saree" },
                  { id: "Dothy", name: "Dothy" },
                  { id: "Towel", name: "Towel" },
                  { id: "Gada", name: "Gada" },
                  { id: "Home Furnishing", name: "Home Furnishing" },
                  { id: "Lungi", name: "Lungi" },
                  { id: "Bedsheets", name: "Bedsheets" },
                  { id: "Any Other (Specify)", name: "Any Other (Specify)" },
                ]}
                value={formData?.variety_produced || []}
                onChange={(value) =>
                  handleInputChange("variety_produced", value)
                }
                placeholder="Select Varieties"
              />
              {errors.variety_produced && (
                <p className="text-sm text-red-500">
                  {errors.variety_produced.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="other_variety"
                required={formData?.variety_produced?.includes(
                  "Any Other (Specify)"
                )}
              >
                Specify the Other Variety Produced If Not Mentioned
              </Label>
              <Input
                id="other_variety"
                value={formData.other_variety || ""}
                onChange={(e) =>
                  handleInputChange("other_variety", e.target.value)
                }
                disabled={
                  !formData?.variety_produced?.includes("Any Other (Specify)")
                }
                placeholder="Enter other variety name"
                className={errors.other_variety ? "border-red-500" : ""}
              />
              {errors.other_variety && (
                <p className="text-sm text-red-500">
                  {errors.other_variety.message}
                </p>
              )}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rapier_looms" required>
                Number of existing Rapier Looms Installed{" "}
              </Label>
              <Input
                id="rapier_looms"
                value={formData.rapier_looms || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 3);
                  handleInputChange("rapier_looms", value);
                }}
                className={errors.rapier_looms ? "border-red-500" : ""}
              />
              {errors.rapier_looms && (
                <p className="text-sm text-red-500">
                  {errors.rapier_looms.message}
                </p>
              )}
            </div>

            {/* <div className="space-y-2">
              <Label htmlFor="cost_paid_for_loom">
                Expenditure incurred for Existing loom
              </Label>
              <Input
                id="cost_paid_for_loom"
                value={formData.cost_paid_for_loom || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 8);
                  handleInputChange("cost_paid_for_loom", value);
                }}
                className={errors.cost_paid_for_loom ? "border-red-500" : ""}
              />
              {errors.cost_paid_for_loom && (
                <p className="text-sm text-red-500">
                  {errors.cost_paid_for_loom.message}
                </p>
              )}
            </div> */}
          </div>

          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default LoomDetails;
